import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { INVITATION_TYPES } from '../../lib/invitation-types.js';
import { css, html, nothing } from '../../lib/lit.js';

// Bootstrap badge class per invitation status.
const STATUS_BADGE = {
    pending: 'text-bg-warning',
    accepted: 'text-bg-success',
    revoked: 'text-bg-secondary',
    expired: 'text-bg-secondary',
};

function formatDate(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

// Staff home: create an invitation and see the status of the ones already sent.
// The invitation itself, the email, and the accept flow all live in d3-auth;
// this view posts to the Staff API (which calls d3-auth over M2M) and reads the
// list back from it.
class InvitationsView extends BaseComponent {
    static properties = {
        _email: { state: true },
        _type: { state: true },
        _submitting: { state: true },
        _error: { state: true },
        /** @type {object|null} The just-created invitation, or null before one is sent. */
        _issued: { state: true },
        _copied: { state: true },
        /** @type {Array<object>} */
        _invitations: { state: true },
        _loadingList: { state: true },
        _listError: { state: true },
    };

    static styles = css`
        :host {
            display: block;
        }
        .wrap {
            max-width: 52rem;
            margin: 0 auto;
            padding: 2rem 1rem 4rem;
        }
        .form-col {
            max-width: 32rem;
        }
        .hint {
            font-size: 0.85rem;
        }
        .link-box {
            font-family: ui-monospace, monospace;
            font-size: 0.85rem;
            word-break: break-all;
        }
        table {
            font-size: 0.9rem;
        }
    `;

    constructor() {
        super();
        this._email = '';
        this._type = INVITATION_TYPES[0].slug;
        this._submitting = false;
        this._error = '';
        this._issued = null;
        this._copied = false;
        this._invitations = [];
        this._loadingList = true;
        this._listError = '';
    }

    connectedCallback() {
        super.connectedCallback();
        this._loadList();
    }

    async _loadList() {
        this._loadingList = true;
        this._listError = '';
        try {
            const body = await api.get('/invitations');
            this._invitations = body.invitations;
        } catch (error) {
            this._listError = error.message;
        } finally {
            this._loadingList = false;
        }
    }

    async _submit(event) {
        event.preventDefault();
        this._error = '';
        this._submitting = true;
        try {
            this._issued = await api.post('/invitations', {
                email: this._email.trim(),
                type: this._type,
            });
            // The new one should show up as pending in the list.
            this._loadList();
        } catch (error) {
            this._error = error.message;
        } finally {
            this._submitting = false;
        }
    }

    _reset() {
        this._email = '';
        this._type = INVITATION_TYPES[0].slug;
        this._error = '';
        this._issued = null;
        this._copied = false;
    }

    async _copyLink() {
        try {
            await navigator.clipboard.writeText(this._issued.signupLink);
            this._copied = true;
        } catch {
            // Clipboard can be blocked (permissions, insecure context); the link
            // is on screen to copy by hand, so this is not worth surfacing.
        }
    }

    render() {
        return html`
            <div class="wrap">
                <h1 class="h4 mb-4">Invite someone</h1>
                <div class="form-col">
                    ${this._issued ? this._renderIssued() : this._renderForm()}
                </div>
                ${this._renderList()}
            </div>
        `;
    }

    _renderForm() {
        const selected = INVITATION_TYPES.find((t) => t.slug === this._type);
        return html`
            <form @submit=${this._submit}>
                <div class="mb-3">
                    <label class="form-label" for="email">Email address</label>
                    <input
                        id="email"
                        class="form-control"
                        type="email"
                        required
                        autocomplete="off"
                        placeholder="person@example.com"
                        .value=${this._email}
                        ?disabled=${this._submitting}
                        @input=${(e) => {
                            this._email = e.target.value;
                        }}
                    />
                </div>

                <div class="mb-3">
                    <label class="form-label" for="type">Invitation type</label>
                    <select
                        id="type"
                        class="form-select"
                        .value=${this._type}
                        ?disabled=${this._submitting}
                        @change=${(e) => {
                            this._type = e.target.value;
                        }}
                    >
                        ${INVITATION_TYPES.map(
                            (t) => html`<option value=${t.slug}>${t.label}</option>`,
                        )}
                    </select>
                    <div class="hint form-text">${selected?.hint}</div>
                </div>

                ${
                    this._error
                        ? html`<div class="alert alert-danger" role="alert">${this._error}</div>`
                        : nothing
                }

                <button class="btn btn-primary" type="submit" ?disabled=${this._submitting}>
                    ${this._submitting ? 'Sending…' : 'Send invitation'}
                </button>
            </form>
        `;
    }

    _renderIssued() {
        const type = INVITATION_TYPES.find((t) => t.slug === this._issued.type);
        return html`
            <div class="card shadow-sm">
                <div class="card-body">
                    <div class="d-flex align-items-start gap-2 mb-3">
                        <i class="bi bi-check-circle-fill text-success fs-4"></i>
                        <div>
                            <h2 class="h5 mb-1">Invitation sent</h2>
                            <p class="text-muted mb-0">
                                ${this._issued.email} was invited as
                                <strong>${type?.label ?? this._issued.type}</strong>.
                                ${
                                    this._issued.emailSent
                                        ? 'An email with the link is on its way.'
                                        : nothing
                                }
                            </p>
                        </div>
                    </div>

                    <label class="form-label">Invite link</label>
                    <div class="input-group">
                        <input class="form-control link-box" readonly .value=${this._issued.signupLink} />
                        <button class="btn btn-outline-secondary" type="button" @click=${this._copyLink}>
                            ${this._copied ? 'Copied' : 'Copy'}
                        </button>
                    </div>
                    <div class="form-text">
                        d3-auth also emails this link; copy it here if you'd rather send it yourself.
                    </div>

                    <button class="btn btn-primary mt-4" type="button" @click=${this._reset}>
                        Invite someone else
                    </button>
                </div>
            </div>
        `;
    }

    _renderList() {
        return html`
            <div class="mt-5">
                <div class="d-flex align-items-center gap-2 mb-3">
                    <h2 class="h5 mb-0">Invitations</h2>
                    <button
                        class="btn btn-sm btn-outline-secondary"
                        type="button"
                        ?disabled=${this._loadingList}
                        @click=${this._loadList}
                    >
                        Refresh
                    </button>
                </div>
                ${this._renderListBody()}
            </div>
        `;
    }

    _renderListBody() {
        if (this._listError) {
            return html`<div class="alert alert-danger" role="alert">${this._listError}</div>`;
        }
        if (this._loadingList && this._invitations.length === 0) {
            return html`<p class="text-muted">Loading…</p>`;
        }
        if (this._invitations.length === 0) {
            return html`<p class="text-muted">No invitations yet.</p>`;
        }
        return html`
            <div class="table-responsive">
                <table class="table table-sm align-middle">
                    <thead>
                        <tr>
                            <th>Email</th>
                            <th>Invited as</th>
                            <th>Status</th>
                            <th>Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._invitations.map(
                            (inv) => html`
                                <tr>
                                    <td>${inv.email}</td>
                                    <td>${inv.appName}</td>
                                    <td>
                                        <span class="badge ${STATUS_BADGE[inv.status] ?? 'text-bg-secondary'}">
                                            ${inv.status}
                                        </span>
                                    </td>
                                    <td class="text-muted">${formatDate(inv.createdAt)}</td>
                                </tr>
                            `,
                        )}
                    </tbody>
                </table>
            </div>
        `;
    }
}

customElements.define('d3-invitations-view', InvitationsView);
